import { Star } from "lucide-react";
import { StatusChip } from "@/components/StatusChip";
import { useApi } from "@/hooks/useApi";
import { dealerService } from "@/services/DealerService";
import { DealerContactDetail } from "@/types/dealer";
import { DealerTabSection, DealerTabState } from "./DealerTabState";

const displayValue = (value: string | null): string => value?.trim() || "-";

const sortPreferredFirst = (contacts: DealerContactDetail[]): DealerContactDetail[] => [
  ...contacts.filter((contact) => contact.isPreferredContact),
  ...contacts.filter((contact) => !contact.isPreferredContact),
];

function ContactLink({ value, scheme }: { value: string | null; scheme: "tel" | "mailto" }) {
  const trimmed = value?.trim();
  if (!trimmed) return <>-</>;

  return (
    <a href={`${scheme}:${trimmed}`} className="text-primary-700 hover:underline">
      {trimmed}
    </a>
  );
}

function ContactRoles({ contact }: { contact: DealerContactDetail }) {
  const roles = [
    contact.isDealerUser && "Dealer user",
    contact.isDealerManager && "Manager",
    contact.isSuperUser && "Super user",
  ].filter((role): role is string => Boolean(role));

  if (roles.length === 0) return <>-</>;

  return (
    <div className="flex flex-wrap gap-1">
      {roles.map((role) => (
        <StatusChip key={role} status={role} variant="pastel" hideIcon />
      ))}
    </div>
  );
}

export function ContactsTab({ dealerId }: { dealerId: number }) {
  const { data, loading, error } = useApi((signal) => dealerService.getDealerContacts(dealerId, signal), [dealerId]);

  return (
    <DealerTabState
      data={data}
      loading={loading}
      error={error}
      label="contacts"
      emptyMessage="No contacts found for this dealer."
    >
      {(contacts) => (
        <DealerTabSection title="Contacts" count={contacts.length}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-max text-sm">
              <thead>
                <tr className="bg-muted/40 border-b text-left">
                  <th className="p-3 font-medium">Name</th>
                  <th className="p-3 font-medium">Position</th>
                  <th className="p-3 font-medium">Phone</th>
                  <th className="p-3 font-medium">Mobile</th>
                  <th className="p-3 font-medium">Email</th>
                  <th className="p-3 font-medium">Roles</th>
                </tr>
              </thead>
              <tbody>
                {sortPreferredFirst(contacts).map((contact) => (
                  <tr key={contact.contactId} className="border-b last:border-b-0">
                    <td className="p-3 font-semibold">
                      <div className="flex items-center gap-1">
                        {displayValue(contact.name)}
                        {contact.isPreferredContact && (
                          <Star
                            role="img"
                            aria-label="Preferred contact"
                            className="size-4 fill-amber-400 text-amber-500"
                          />
                        )}
                      </div>
                    </td>
                    <td className="p-3">{displayValue(contact.dealerContactPosition)}</td>
                    <td className="p-3">
                      <ContactLink value={contact.phoneNumber} scheme="tel" />
                    </td>
                    <td className="p-3">
                      <ContactLink value={contact.mobileNumber} scheme="tel" />
                    </td>
                    <td className="p-3">
                      <ContactLink value={contact.emailAddress} scheme="mailto" />
                    </td>
                    <td className="p-3">
                      <ContactRoles contact={contact} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DealerTabSection>
      )}
    </DealerTabState>
  );
}
